import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SubscriptionFilter from "@/components/SubscriptionFilter";
import {
  DollarSign,
  Layers,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  Beaker,
  Trash2,
  Brain,
  Download,
  Gauge,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Zap,
  Tag,
  Clock,
  Lightbulb,
  Target,
  ShieldCheck,
  Sparkles,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// AI Briefing Panel with beautiful tiles - matching InsightsHomePage style
function AIBriefingPanel({ narrative }) {
  // Parse the narrative into sections
  const sections = parseNarrative(narrative);
  
  return (
    <div className="space-y-6" data-testid="ai-briefing-panel">
      {/* Header Card - matching InsightsHomePage briefing style */}
      <div 
        className="rounded-xl p-5"
        style={{
          background: "var(--n-surface-2)",
          border: "1px solid color-mix(in srgb, var(--hig-accent) 15%, transparent)",
        }}
      >
        <div className="flex items-center gap-4">
          <div 
            className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "color-mix(in srgb, var(--hig-accent) 15%, transparent)" }}
          >
            <Brain className="w-5 h-5" style={{ color: "var(--hig-accent)" }} />
          </div>
          <div>
            <h2 className="text-[15px] font-semibold leading-snug flex items-center gap-2">
              AI Cost Intelligence Briefing
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            </h2>
            <p className="text-xs text-[var(--n-text-secondary)] mt-1">
              Powered by AI analysis of your Azure spending patterns
            </p>
          </div>
        </div>
      </div>
      
      {/* Tiles Grid - matching InsightsHomePage card grid */}
      {sections.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {sections.map((section, index) => (
            <BriefingTile key={index} section={section} index={index} />
          ))}
        </div>
      )}
      
      {/* Fallback for unparseable content */}
      {sections.length === 0 && narrative && (
        <div 
          className="rounded-xl p-6"
          style={{
            background: "var(--n-surface-2)",
            border: "1px solid var(--n-border-2)",
          }}
        >
          <p className="text-xs text-[var(--n-text-secondary)] whitespace-pre-wrap leading-relaxed">
            {narrative}
          </p>
        </div>
      )}
    </div>
  );
}

function parseNarrative(narrative) {
  if (!narrative) return [];
  
  const sections = [];
  const lines = narrative.split('\n');
  let currentSection = null;
  let currentContent = [];
  
  for (const line of lines) {
    // Check for headers (##, ###, or bold **text**)
    const h2Match = line.match(/^##\s+(.+)/);
    const h3Match = line.match(/^###\s+(.+)/);
    const boldMatch = line.match(/^\*\*(.+)\*\*:?\s*(.*)/);
    const numberMatch = line.match(/^(\d+)\.\s+\*\*(.+)\*\*:?\s*(.*)/);
    
    if (h2Match || h3Match) {
      if (currentSection) {
        sections.push({ title: currentSection, content: currentContent.join('\n').trim(), type: 'section' });
      }
      currentSection = (h2Match || h3Match)[1].trim();
      currentContent = [];
    } else if (numberMatch) {
      // Numbered recommendation
      if (currentSection && currentContent.length > 0) {
        sections.push({ title: currentSection, content: currentContent.join('\n').trim(), type: 'section' });
        currentContent = [];
      }
      sections.push({
        title: numberMatch[2].trim(),
        content: numberMatch[3].trim(),
        type: 'recommendation',
        number: numberMatch[1]
      });
      currentSection = null;
    } else if (boldMatch && !currentSection) {
      // Standalone bold item (like a finding)
      sections.push({
        title: boldMatch[1].trim(),
        content: boldMatch[2].trim(),
        type: 'finding'
      });
    } else if (line.trim()) {
      currentContent.push(line);
    }
  }
  
  // Add final section
  if (currentSection && currentContent.length > 0) {
    sections.push({ title: currentSection, content: currentContent.join('\n').trim(), type: 'section' });
  }
  
  // If we couldn't parse any sections, create one from the whole narrative
  if (sections.length === 0 && narrative.trim()) {
    // Split by sentences and create tiles
    const sentences = narrative.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 20);
    sentences.slice(0, 6).forEach((sentence, i) => {
      sections.push({
        title: `Insight ${i + 1}`,
        content: sentence.trim(),
        type: 'insight'
      });
    });
  }
  
  return sections.slice(0, 9); // Max 9 tiles
}

// Category colors matching InsightsHomePage CAT pattern
const TILE_STYLES = {
  savings: { accent: "var(--hig-success)", keywords: ['saving', 'reduce', 'cut', 'optim'] },
  risk: { accent: "var(--hig-warning)", keywords: ['risk', 'alert', 'warning', 'issue', 'concern'] },
  recommendation: { accent: "#af52de", keywords: ['recommend', 'action', 'should', 'consider'] },
  trend: { accent: "var(--hig-accent)", keywords: ['trend', 'growth', 'increase', 'decrease'] },
  efficiency: { accent: "#00b4d8", keywords: ['efficien', 'waste', 'orphan'] },
  security: { accent: "var(--hig-danger)", keywords: ['security', 'compli', 'govern'] },
  default: { accent: "var(--hig-accent)", keywords: [] },
};

function getTileStyle(title, content) {
  const text = (title + ' ' + content).toLowerCase();
  
  for (const [key, style] of Object.entries(TILE_STYLES)) {
    if (key === 'default') continue;
    if (style.keywords.some(kw => text.includes(kw))) {
      return style;
    }
  }
  return TILE_STYLES.default;
}

function getTileIcon(title, content, type) {
  const text = (title + ' ' + content).toLowerCase();
  
  if (text.includes('saving') || text.includes('cost') || text.includes('spend')) return DollarSign;
  if (text.includes('risk') || text.includes('alert') || text.includes('warning')) return AlertTriangle;
  if (text.includes('recommend') || text.includes('action')) return Lightbulb;
  if (text.includes('trend') || text.includes('growth')) return TrendingUp;
  if (text.includes('optim') || text.includes('efficien')) return Target;
  if (text.includes('security') || text.includes('compli')) return ShieldCheck;
  if (type === 'recommendation') return CheckCircle2;
  
  return Info;
}

function BriefingTile({ section, index }) {
  const { title, content, type, number } = section;
  const style = getTileStyle(title, content);
  const Icon = getTileIcon(title, content, type);
  
  // Clean up content (remove markdown artifacts)
  const cleanContent = content
    .replace(/\*\*/g, '')
    .replace(/^\s*[-*]\s*/gm, '• ')
    .replace(/`([^`]+)`/g, '$1')
    .trim();
  
  return (
    <div
      className={`group relative flex flex-col rounded-xl animate-card-reveal stagger-${Math.min(index + 1, 6)}`}
      style={{
        background: "var(--n-surface-2)",
        border: `1px solid color-mix(in srgb, ${style.accent} 15%, transparent)`,
      }}
      data-testid={`briefing-tile-${index}`}
    >
      {/* Accent bar - matching InsightsHomePage */}
      <div 
        className="h-px w-full rounded-t-xl" 
        style={{ 
          background: `linear-gradient(90deg, color-mix(in srgb, ${style.accent} 40%, transparent), color-mix(in srgb, ${style.accent} 10%, transparent), transparent)` 
        }} 
      />

      <div className="p-5 flex-1 flex flex-col">
        {/* Header row: icon + number badge */}
        <div className="flex items-center justify-between mb-4">
          <div 
            className="w-9 h-9 rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform"
            style={{ background: `color-mix(in srgb, ${style.accent} 15%, transparent)` }}
          >
            {number ? (
              <span className="text-sm font-bold" style={{ color: style.accent }}>{number}</span>
            ) : (
              <Icon className="w-4 h-4" style={{ color: style.accent }} />
            )}
          </div>
          {type === 'recommendation' && (
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: style.accent }} />
              <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: style.accent }}>Action</span>
            </div>
          )}
        </div>

        {/* Title */}
        <h3 className="text-[13px] font-semibold leading-snug mb-2.5 line-clamp-2">{title}</h3>

        {/* Description */}
        <p className="text-[11px] text-[var(--n-text-secondary)] leading-relaxed flex-1 line-clamp-4">
          {cleanContent || 'Analysis insight'}
        </p>
      </div>
    </div>
  );
}

function TrendChart({ months, totals, momGrowth }) {
  if (!totals || totals.length === 0) return null;
  const max = Math.max(...totals, 1);
  return (
    <div className="space-y-3" data-testid="trend-chart">
      <div className="flex items-end gap-1.5 h-40">
        {totals.map((val, i) => {
          const pct = (val / max) * 100;
          const growth = momGrowth?.[i] || 0;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <span className={`text-[10px] font-mono ${growth > 0 ? "text-red-600 dark:text-red-400" : growth < 0 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                {growth > 0 ? "+" : ""}{growth}%
              </span>
              <div className="w-full relative flex items-end h-32">
                <div
                  className={`w-full rounded-t transition-all duration-500 ${growth > 5 ? "bg-red-500/70" : growth < -5 ? "bg-emerald-500/70" : "bg-[var(--hig-accent)]/60"}`}
                  style={{ height: `${Math.max(pct, 4)}%` }}
                />
              </div>
              <span className="text-[10px] text-muted-foreground truncate w-full text-center">
                {months?.[i]?.slice(5) || ""}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EfficiencyRing({ score }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? "var(--hig-accent)" : score >= 60 ? "#fbbf24" : "var(--hig-danger)";
  return (
    <div className="relative w-36 h-36 mx-auto" data-testid="efficiency-ring">
      <svg className="w-36 h-36 -rotate-90" viewBox="0 0 128 128">
        <circle cx="64" cy="64" r={radius} fill="none" stroke="var(--n-border-2)" strokeWidth="10" />
        <circle cx="64" cy="64" r={radius} fill="none" stroke={color} strokeWidth="10" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold font-mono" style={{ color }}>{score}</span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Score</span>
      </div>
    </div>
  );
}

function WasteBreakdown({ wastage, totalCost }) {
  if (!wastage) return null;
  const items = [
    { label: "Orphan Resources", cost: wastage.orphan_cost, count: wastage.orphan_count, icon: Trash2, color: "text-red-600 dark:text-red-400", bg: "bg-red-500/10" },
    { label: "Untagged Overhead", cost: wastage.untagged_cost, count: wastage.untagged_count, icon: Tag, color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/10" },
    { label: "Stale Resources", cost: wastage.stale_cost, count: wastage.stale_count, icon: Clock, color: "text-violet-600 dark:text-violet-400", bg: "bg-violet-500/10" },
  ];
  return (
    <div className="space-y-3" data-testid="waste-breakdown">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${item.bg} shrink-0`}>
            <item.icon className={`w-3.5 h-3.5 ${item.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">{item.label}</span>
              <span className="text-xs font-mono text-foreground">${item.cost?.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1 bg-[var(--n-border-2)] rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${item.color.replace("text-", "bg-").replace("-400", "-500")}`} style={{ width: `${Math.min((item.cost / Math.max(totalCost, 1)) * 100, 100)}%` }} />
              </div>
              <span className="text-[10px] text-muted-foreground shrink-0">{item.count} items</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CostsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [timeframe, setTimeframe] = useState("Last30Days");
  const [seeding, setSeeding] = useState(false);
  const [trends, setTrends] = useState(null);
  const [efficiency, setEfficiency] = useState(null);
  const [aiNarrative, setAiNarrative] = useState(null);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ timeframe });
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().get(`/costs?${params}`);
      setCosts(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to fetch cost data";
      toast.error(msg);
      setCosts({ costs: [], total_cost: 0, currency: "USD", errors: [{ error: msg }] });
    } finally {
      setLoading(false);
    }
  }, [api, subscriptionId, timeframe]);

  const fetchTrends = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().get(`/costs/trends?${params}`);
      setTrends(res.data);
    } catch { setTrends(null); }
  }, [api, subscriptionId]);

  const fetchEfficiency = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().get(`/costs/efficiency?${params}`);
      setEfficiency(res.data);
    } catch { setEfficiency(null); }
  }, [api, subscriptionId]);

  useEffect(() => {
    fetchCosts();
    fetchTrends();
    fetchEfficiency();
  }, [fetchCosts, fetchTrends, fetchEfficiency]);

  const seedDemoCosts = async () => {
    setSeeding(true);
    try {
      const res = await api().post("/costs/seed-demo");
      toast.success(res.data.message);
      fetchCosts();
      fetchTrends();
      fetchEfficiency();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to seed demo data");
    } finally {
      setSeeding(false);
    }
  };

  const runAIAnalysis = async () => {
    setAnalyzingAI(true);
    try {
      const params = new URLSearchParams();
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().post(`/costs/ai-analysis?${params}`);
      setAiNarrative(res.data);
      toast.success("AI analysis complete");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI analysis failed");
    } finally {
      setAnalyzingAI(false);
    }
  };

  const exportPPTX = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      const res = await api().post(`/costs/export-pptx?${params}`, null, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `niyant_cost_report_${new Date().toISOString().slice(0, 10)}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const latestGrowth = trends?.mom_growth?.slice(-1)?.[0] || 0;
  const GrowthIcon = latestGrowth > 0 ? ArrowUpRight : latestGrowth < 0 ? ArrowDownRight : Minus;
  const growthColor = latestGrowth > 0 ? "text-red-600 dark:text-red-400" : latestGrowth < 0 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground";

  return (
    <div className="space-y-6" data-testid="costs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Cost Intelligence</h1>
          <p className="text-[11px] text-[var(--n-text-muted)] mt-0.5">AI-driven spend analysis, trends & optimization</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SubscriptionFilter value={subscriptionId} onChange={setSubscriptionId} />
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-24 bg-[var(--n-surface)] border-[var(--n-border)] text-xs h-8" data-testid="timeframe-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Last7Days">7d</SelectItem>
              <SelectItem value="Last30Days">30d</SelectItem>
              <SelectItem value="Last90Days">90d</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={() => { fetchCosts(); fetchTrends(); fetchEfficiency(); }} disabled={loading} className="h-8 w-8 text-[var(--n-text-dim)] hover:text-[var(--hig-accent)]" data-testid="refresh-costs-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Demo badge */}
      {costs?.is_demo && (
        <div className="flex items-start gap-2 bg-violet-500/5 border border-violet-500/20 rounded-lg px-3 py-2 text-xs" data-testid="demo-data-badge">
          <Beaker className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400 shrink-0 mt-0.5" />
          <span className="text-violet-600 dark:text-violet-400">Showing demo cost data. Real data appears with Cost Management Reader access.</span>
        </div>
      )}

      {/* Errors */}
      {costs?.errors?.length > 0 && (
        <div className="space-y-2">
          {costs.errors.map((err, i) => (
            <div key={i} className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 text-xs">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <span className="text-amber-600 dark:text-amber-400">{err.credential ? `${err.credential}: ` : ""}{err.error}</span>
            </div>
          ))}
        </div>
      )}

      {/* KPI Cards Row */}
      {costs && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)] flex flex-col">
            <CardContent className="p-5 flex-1">
              <div className="flex items-center justify-between">
                <div className="p-2.5 rounded-xl bg-emerald-500/10"><DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /></div>
                {latestGrowth !== 0 && <div className={`flex items-center gap-0.5 text-xs font-mono ${growthColor}`}><GrowthIcon className="w-3 h-3" />{Math.abs(latestGrowth)}%</div>}
              </div>
              <p className="text-xl lg:text-2xl font-bold font-mono mt-4">${costs.total_cost?.toLocaleString()}</p>
              <p className="text-[11px] text-muted-foreground mt-1.5">Total Spend</p>
            </CardContent>
          </Card>
          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)] flex flex-col">
            <CardContent className="p-5 flex-1">
              <div className="p-2.5 rounded-xl bg-[var(--hig-accent)]/10 w-fit"><Layers className="w-4 h-4 text-[var(--hig-accent)]" /></div>
              <p className="text-xl lg:text-2xl font-bold font-mono mt-4">{costs.costs?.length || 0}</p>
              <p className="text-[11px] text-muted-foreground mt-1.5">Resource Groups</p>
            </CardContent>
          </Card>
          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)] flex flex-col">
            <CardContent className="p-5 flex-1">
              <div className="p-2.5 rounded-xl bg-amber-500/10 w-fit"><Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" /></div>
              <p className="text-xl lg:text-2xl font-bold font-mono mt-4">${efficiency?.savings_potential?.toLocaleString() || "0"}</p>
              <p className="text-[11px] text-muted-foreground mt-1.5">Savings Potential</p>
            </CardContent>
          </Card>
          <Card className="bg-[var(--n-card)] border-[var(--n-border-2)] flex flex-col">
            <CardContent className="p-5 flex-1">
              <div className="p-2.5 rounded-xl bg-violet-500/10 w-fit"><Gauge className="w-4 h-4 text-violet-600 dark:text-violet-400" /></div>
              <p className="text-xl lg:text-2xl font-bold font-mono mt-4">{efficiency?.efficiency_score || "-"}%</p>
              <p className="text-[11px] text-muted-foreground mt-1.5">Efficiency Score</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Analysis + Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={runAIAnalysis} disabled={analyzingAI} size="sm" className="bg-[var(--hig-accent)] hover:opacity-90" data-testid="run-ai-analysis-btn">
          <Brain className={`w-3.5 h-3.5 mr-1.5 ${analyzingAI ? "animate-pulse" : ""}`} />
          {analyzingAI ? "Analyzing..." : "AI Cost Analysis"}
        </Button>
        <Button onClick={exportPPTX} disabled={exporting} variant="outline" size="sm" className="border-[var(--n-border-2)]" data-testid="export-pptx-btn">
          <Download className={`w-3.5 h-3.5 mr-1.5 ${exporting ? "animate-spin" : ""}`} />
          {exporting ? "Generating..." : "Export PPTX"}
        </Button>
        {(!costs?.costs?.length && !costs?.is_demo) && (
          <Button onClick={seedDemoCosts} disabled={seeding} variant="outline" size="sm" className="border-violet-500/20 text-violet-600 dark:text-violet-400 hover:bg-violet-500/10" data-testid="seed-demo-costs-btn">
            {seeding ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Generating...</> : <><Beaker className="w-3.5 h-3.5 mr-1" /> Seed Demo Data</>}
          </Button>
        )}
      </div>

      {/* AI Narrative Panel - Enhanced with Tiles */}
      {aiNarrative?.narrative && (
        <AIBriefingPanel narrative={aiNarrative.narrative} />
      )}

      {/* Main Grid: Trend + Efficiency */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)] flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[var(--hig-accent)]" /> Monthly Trend
              {trends?.avg_monthly > 0 && <Badge variant="outline" className="text-[10px] border-[var(--n-border-2)] ml-auto">Avg: ${trends.avg_monthly?.toLocaleString()}/mo</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-center min-h-[220px]">
            {trends?.totals?.length > 0 ? (
              <TrendChart months={trends.months} totals={trends.totals} momGrowth={trends.mom_growth} />
            ) : (
              <div className="py-10 text-center">
                <div className="mx-auto w-12 h-12 rounded-xl bg-[var(--hig-accent)]/10 flex items-center justify-center mb-4">
                  <TrendingUp className="w-5 h-5 text-[var(--hig-accent)]" />
                </div>
                <h4 className="text-sm font-medium mb-1">No trend data yet</h4>
                <p className="text-xs text-muted-foreground max-w-[200px] mx-auto">
                  Cost trends will appear once you have historical spending data.
                </p>
              </div>
            )}
        </CardContent>
        </Card>

        {/* Efficiency + Wastage */}
        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)] flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Gauge className="w-4 h-4 text-[var(--hig-accent)]" /> Efficiency & Wastage
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-center min-h-[220px]">
            {efficiency ? (
              <div className="grid grid-cols-2 gap-4">
                <EfficiencyRing score={efficiency.efficiency_score} />
                <WasteBreakdown wastage={efficiency.wastage} totalCost={efficiency.total_cost} />
              </div>
            ) : (
              <div className="py-10 text-center">
                <div className="mx-auto w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mb-4">
                  <Gauge className="w-5 h-5 text-violet-500" />
                </div>
                <h4 className="text-sm font-medium mb-1">No efficiency data yet</h4>
                <p className="text-xs text-muted-foreground max-w-[200px] mx-auto">
                  Efficiency scores require resource discovery data.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cost Breakdown Table */}
      <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Cost by Resource Group
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (<div key={i} className="h-10 bg-[var(--n-border-2)] rounded animate-pulse" />))}
            </div>
          ) : !costs?.costs?.length ? (
            <div className="py-12 text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-5">
                <DollarSign className="w-7 h-7 text-emerald-500" />
              </div>
              <h3 className="text-sm font-semibold mb-2">No cost data available</h3>
              <p className="text-xs text-muted-foreground max-w-xs mx-auto mb-5 leading-relaxed">
                Connect an Azure subscription with Cost Management Reader access, or generate demo data to preview the experience.
              </p>
              <Button 
                onClick={seedDemoCosts} 
                disabled={seeding} 
                size="sm"
                className="border-violet-500/20 text-violet-600 dark:text-violet-400 hover:bg-violet-500/10"
                variant="outline"
                data-testid="seed-demo-costs-empty-btn"
              >
                {seeding ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating...</> : <><Beaker className="w-3.5 h-3.5 mr-1.5" /> Generate Demo Data</>}
              </Button>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
              {costs.costs.map((item, i) => {
                const maxCost = costs.costs[0]?.cost || 1;
                const pct = (item.cost / maxCost) * 100;
                return (
                  <div key={i} className="space-y-1.5" data-testid={`cost-row-${i}`}>
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 truncate max-w-[60%]">
                        <span className="text-foreground font-medium truncate">{item.resource_group}</span>
                        {item.subscription_name && <Badge variant="outline" className="text-[10px] border-[var(--n-border-2)] text-muted-foreground shrink-0">{item.subscription_name}</Badge>}
                      </div>
                      <span className="font-mono text-foreground font-medium shrink-0">{item.currency} {item.cost.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 bg-[var(--n-border-2)] rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cleanup CTA */}
      <Card className="bg-amber-500/[0.03] border-amber-500/20">
        <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10 shrink-0"><Trash2 className="w-4 h-4 text-amber-600 dark:text-amber-400" /></div>
            <div>
              <p className="text-sm font-medium">Reduce costs with Orphan Cleanup</p>
              <p className="text-xs text-muted-foreground mt-0.5">Identify unused resources and generate deletion scripts to reclaim spend.</p>
            </div>
          </div>
          <Button onClick={() => navigate("/cleanup")} size="sm" className="bg-amber-500 hover:bg-amber-600 text-primary-foreground font-semibold shrink-0" data-testid="costs-cleanup-wizard-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Cleanup Wizard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
